import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ProgramacionMasivaDetalle } from 'src/app/domain/model/agenda/programacion-masiva';
import { pmErroresDialogPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * Componente Dumb: Diálogo de resultados de programación
 *
 * Muestra TODOS los asesores con sus resultados (éxitos y errores).
 * NO tiene lógica de negocio ni servicios inyectados.
 *
 * Legacy: ListadoErroresProgramacionMasivaViewImpl.java
 * Mensajes dinámicos:
 * - msgSiErroresPM: "Se encontraron asesores que no pudieron ser programados o se encuentran pendientes por programar"
 * - msgNoErroresPM: "La cita ha sido programada exitosamente para todos los consultores seleccionados"
 */
@Component({
  selector: 'app-pm-errores-dialog',
  standalone: true,
  imports: [CommonModule, ButtonModule, DialogModule, TableModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['../../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      header="Listado de Consultores"
      [visible]="visible"
      (visibleChange)="visibleChange.emit($event)"
      [modal]="true"
      [style]="{ width: '95vw', 'max-width': '60rem' }"
      [breakpoints]="{ '960px': '90vw', '640px': '95vw' }"
      [draggable]="false"
    >
      <!-- Mensaje dinámico según legacy: msgSiErroresPM / msgNoErroresPM -->
      @if (cantidadErrores > 0) {
        <div
          class="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded text-yellow-800 dark:text-yellow-200"
        >
          <i class="pi pi-exclamation-triangle mr-2"></i>
          Se encontraron consultores que no pudieron ser programados o se
          encuentran pendientes por programar
        </div>
      } @else {
        <div
          class="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded text-green-800 dark:text-green-200"
        >
          <i class="pi pi-check-circle mr-2"></i>
          La cita ha sido programada exitosamente para todos los consultores
          seleccionados
        </div>
      }

      <div class="mb-2 text-sm text-gray-600 dark:text-gray-400">
        Registros del 1 hasta el {{ asesores.length }} de {{ asesores.length }}
      </div>

      <div class="md:hidden space-y-3 p-3">
        @if (asesores.length === 0) {
          <div class="text-center text-gray-500 dark:text-gray-400 py-4">
            No hay registros que mostrar
          </div>
        }
        @for (asesor of asesores; track $index) {
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
          >
            <div class="mb-3 min-w-0">
              <div class="text-xs text-gray-500 dark:text-gray-400">
                Consultor
              </div>
              <div
                class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                [attr.title]="tituloTabla(asesor.nombreAsesor)"
              >
                {{ textoTabla(asesor.nombreAsesor) }}
              </div>
            </div>
            <div class="grid grid-cols-1 gap-2 text-sm">
              <div>
                <span class="text-gray-500 dark:text-gray-400">Dni:</span>
                <span class="text-gray-800 dark:text-gray-200">{{
                  textoTabla(asesor.dniAsesor)
                }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">Estado:</span>
                <span
                  class="ml-1"
                  [class]="asesor.error ? 'text-red-600' : 'text-green-600'"
                  >{{
                    textoTabla(asesor.error || 'Programado exitosamente')
                  }}</span
                >
              </div>
            </div>
          </div>
        }
      </div>

      <div class="hidden md:block">
        <div
          role="region"
          [attr.aria-label]="tableConfig.a11y.tableAriaLabel"
          class="sr-only"
        ></div>
        <p-table
          [resizableColumns]="true"
          columnResizeMode="expand"
          [value]="asesores"
          [scrollable]="true"
          [responsiveLayout]="'scroll'"
          [tableStyle]="{
            'min-width': tableConfig.responsive.minWidth || '72rem'
          }"
          [scrollHeight]="tableConfig.responsive.scrollHeight || '320px'"
          [styleClass]="tableStyleClass"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn>
                <span class="grid-header-text" title="Dni Consultor"
                  >Dni Consultor</span
                >
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Consultor"
                  >Consultor</span
                >
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Estado">Estado</span>
              </th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-asesor>
            <tr>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(asesor.dniAsesor)"
                  >{{ textoTabla(asesor.dniAsesor) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(asesor.nombreAsesor)"
                  >{{ textoTabla(asesor.nombreAsesor) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="
                    tituloTabla(asesor.error || 'Programado exitosamente')
                  "
                  [class]="asesor.error ? 'text-red-600' : 'text-green-600'"
                >
                  {{ textoTabla(asesor.error || 'Programado exitosamente') }}
                </span>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td
                colspan="3"
                class="text-center text-gray-500 dark:text-gray-400 py-4"
              >
                No hay registros que mostrar
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="visibleChange.emit(false)"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class PmErroresDialogComponent {
  @Input() visible = false;
  /**
   * Legacy compatibility: acepta tanto 'errores' como 'asesores'
   * @deprecated Usar 'asesores' para consistencia con el legacy
   */
  @Input() set errores(value: ProgramacionMasivaDetalle[]) {
    this._asesores = value;
  }
  @Input() set asesores(value: ProgramacionMasivaDetalle[]) {
    this._asesores = value;
  }
  get asesores(): ProgramacionMasivaDetalle[] {
    return this._asesores;
  }

  @Output() visibleChange = new EventEmitter<boolean>();

  private _asesores: ProgramacionMasivaDetalle[] = [];

  readonly tableConfig = pmErroresDialogPreset.createConfig({
    style: {
      tableClassName: 'tabla-pm-errores',
      striped: true,
      gridlines: false,
      resizableColumns: true,
      rowHover: false
    },
    responsive: {
      mode: 'hybrid',
      minWidth: '72rem',
      scrollHeight: '320px',
      cardBreakpointPx: 768
    },
    a11y: {
      tableAriaLabel: 'Tabla de resultados de programacion masiva',
      announceSelection: false,
      announcePageChanges: false,
      focusFirstActionOnRowSelect: false
    }
  });

  get tableStyleClass(): string {
    const classes = ['p-datatable-sm', this.tableConfig.style.tableClassName];
    if (this.tableConfig.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (this.tableConfig.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  get cantidadErrores(): number {
    return this._asesores.filter((a) => a.error).length;
  }
}
